import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import "moment-duration-format";
import Swal from "sweetalert2";
import { Footprints } from "lucide-react";
import { trackButtonClick } from "../../services/googleSheetsService";

import "./BasicPace.scss";

import { TrackCalculator } from "./components/TrackCalculator";
import FullTimer from "../TaipeiMarathonTimer/components/FullTimer";
import HalfTimer from "../TaipeiMarathonTimer/components/HalfTimer";

// Images
import running_icon_before from "../../assets/images/icons8-human-100.png";
import running_icon_after from "../../assets/images/icons8-jogging-for-cardio-exercise-to-enhance-stamina-96.png";
import station_icon from "../../assets/images/station-icon.png";
import station_icon_noselect from "../../assets/images/station-icon-noSeleted.png";
import runTrack from "../../assets/images/running-track.png";

function BasicPace() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") === "TRACK" ? "TRACK" : "ROAD";

    const setActiveTab = (tab) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("tab", tab);
            return newParams;
        });
    };

    // --- Road Run State ---
    const [raceTime, setRaceTime] = useState({
        hours: "",
        minutes: "",
        seconds: "",
        hydrationTime: "",
    });

    const [results, setResults] = useState({
        hydrationStations: [false, ...Array.from({ length: 15 }, () => true), false],
        note: Array.from({ length: 17 }, () => ""),
        formattedCumulativeTimes: [],
        formattedZoneTimes: [],
    });

    const [results_half, setResults_half] = useState({
        hydrationStations: [false, ...Array.from({ length: 7 }, () => true), false],
        note: Array.from({ length: 9 }, () => ""),
        formattedCumulativeTimes_half: [],
        formattedZoneTimes_half: [],
    });

    const [selectedGroup, setSelectedGroup] = useState("full");

    // --- Handlers ---
    const handleRaceTimeChange = (event, field, min, max) => {
        let value = parseInt(event.target.value) || 0;
        value = Math.min(Math.max(value, min), max);
        setRaceTime((prev) => ({ ...prev, [field]: value }));
    };

    const formatTime = (timeInSeconds) => {
        const duration = moment.duration(timeInSeconds, "seconds");
        return duration.format("HH:mm:ss", { trim: false });
    };

    const formatMMSS = (timeInSeconds) => {
        const duration = moment.duration(timeInSeconds, "seconds");
        return duration.format("mm:ss", { trim: false });
    };

    const handleHydrationStationsChange = (event, index) => {
        const newStations = [...results.hydrationStations];
        newStations[index] = !newStations[index];
        setResults((prev) => ({ ...prev, hydrationStations: newStations }));
    };

    const handleHydrationStationsChange_half = (event, index) => {
        const newStations = [...results_half.hydrationStations];
        newStations[index] = !newStations[index];
        setResults_half((prev) => ({ ...prev, hydrationStations: newStations }));
    };

    const calculateResults = () => {
        let totalRaceTimeInSeconds =
            (parseInt(raceTime.hours) || 0) * 3600 +
            (parseInt(raceTime.minutes) || 0) * 60 +
            (parseInt(raceTime.seconds) || 0);

        // --- Full Marathon ---
        let totalHydrationTime = 0;
        results.hydrationStations.forEach((isChecked) => {
            if (isChecked) totalHydrationTime += parseInt(raceTime.hydrationTime) || 0;
        });

        // Standard Marathon Distance 42.195km
        const averagePace = (totalRaceTimeInSeconds - totalHydrationTime) / 42.195;

        const intervalPaceArr = Array.from({ length: 17 }, (_, index) => index);
        const intervalPace = intervalPaceArr.map(() => formatTime(averagePace));

        const zoneDistances = Array.from({ length: 17 }, (_, index) =>
            index === 16 ? 2.195 : 2.5
        );
        const zoneTimes = zoneDistances.map((distance) => distance * averagePace);

        const cumulativeTimes = [];
        let lastCumulativeTime = 0;
        zoneTimes.forEach((time, index) => {
            const hydration =
                results.hydrationStations[index] && raceTime.hydrationTime !== ""
                    ? parseInt(raceTime.hydrationTime) || 0
                    : 0;
            const cumulativeTimeWithHydration = lastCumulativeTime + time + hydration;
            cumulativeTimes.push(cumulativeTimeWithHydration);
            lastCumulativeTime = cumulativeTimeWithHydration;
        });

        const formattedZoneTimes = intervalPace.map(() => formatMMSS(averagePace));
        const formattedCumulativeTimes = cumulativeTimes.map((time) => formatTime(time));

        setResults((prev) => ({
            ...prev,
            formattedZoneTimes,
            formattedCumulativeTimes,
        }));

        // --- Half Marathon ---
        let totalHydrationTime_half = 0;
        results_half.hydrationStations.forEach((isChecked) => {
            if (isChecked) totalHydrationTime_half += parseInt(raceTime.hydrationTime) || 0;
        });

        // Standard Half Marathon Distance 21.0975km
        const averagePace_half = (totalRaceTimeInSeconds - totalHydrationTime_half) / 21.0975;

        const intervalPaceArr_half = Array.from({ length: 9 }, (_, index) => index);
        const intervalPace_half = intervalPaceArr_half.map(() => formatTime(averagePace_half));

        const zoneDistances_half = Array.from({ length: 9 }, (_, index) =>
            index === 8 ? 1.0975 : 2.5
        );
        const zoneTimes_half = zoneDistances_half.map((distance) => distance * averagePace_half);

        const cumulativeTimes_half = [];
        let lastCumulativeTime_half = 0;
        zoneTimes_half.forEach((time, index) => {
            const hydration =
                results_half.hydrationStations[index] && raceTime.hydrationTime !== ""
                    ? parseInt(raceTime.hydrationTime) || 0
                    : 0;
            const cumulativeTimeWithHydration = lastCumulativeTime_half + time + hydration;
            cumulativeTimes_half.push(cumulativeTimeWithHydration);
            lastCumulativeTime_half = cumulativeTimeWithHydration;
        });

        const formattedZoneTimes_half = intervalPace_half.map(() => formatMMSS(averagePace_half));
        const formattedCumulativeTimes_half = cumulativeTimes_half.map((time) => formatTime(time));

        setResults_half((prev) => ({
            ...prev,
            formattedZoneTimes_half,
            formattedCumulativeTimes_half,
        }));
    };

    const chrckInput = () => {
        if (!raceTime.hours && !raceTime.minutes && !raceTime.seconds) {
            Swal.fire({
                icon: "info",
                text: "請輸入預計完賽時間",
            });
            return true;
        }
        return false;
    };

    const handleSubmit = () => {
        trackButtonClick("通用配速-計算配速", "GO");
        if (chrckInput()) return;
        calculateResults();
    };

    const handleShowMap = () => {
        Swal.fire({ icon: "info", text: "一般路跑模式不提供特定賽道地圖" });
    };

    const showYoutubeVideo = () => {
        Swal.fire({ icon: "info", text: "一般路跑模式不提供特定賽道影片" });
    };

    return (
        <div className="content-section basic-section">
            {/* 頁面標題 */}
            <div className="max-w-4xl mx-auto mb-1 mt-5 md:mt-8 text-center">
                <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">
                    通用配速計算機
                </h1>
                <p className="text-sm md:text-base text-gray-500 mb-0">General Pace Calculator</p>
            </div>

            {/* 切換按鈕 */}
            <div className="   pace-tab-container">
                <div className="flex flex-col md:flex-row gap-3 p-2 bg-gray-50 rounded-2xl border border-gray-200">
                    <button
                        onClick={() => {
                            setActiveTab("ROAD");
                            trackButtonClick("通用配速-切換模式", "一般路跑");
                        }}
                        className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${
                            activeTab === "ROAD"
                                ? "bg-amber-50 text-amber-700 shadow-md ring-2 ring-amber-200"
                                : "bg-transparent text-gray-500 hover:bg-amber-50/30 hover:text-gray-700"
                        }`}
                    >
                        <div
                            className={`p-2.5 rounded-lg transition-colors duration-300 ${
                                activeTab === "ROAD" ? "bg-amber-100" : "bg-gray-100"
                            }`}
                        >
                            <Footprints
                                size={24}
                                className={`transition-colors duration-300 ${
                                    activeTab === "ROAD" ? "text-amber-600" : "text-gray-400"
                                }`}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="font-bold text-lg tracking-wide">一般路跑</span>
                            <span className="text-xs text-gray-400 font-medium">Road Running</span>
                        </div>
                    </button>

                    <button
                        onClick={() => {
                            setActiveTab("TRACK");
                            trackButtonClick("通用配速-切換模式", "操場跑道");
                        }}
                        className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${
                            activeTab === "TRACK"
                                ? "bg-red-50 text-red-700 shadow-md ring-2 ring-red-200"
                                : "bg-transparent text-gray-500 hover:bg-red-50/30 hover:text-gray-700"
                        }`}
                    >
                        <div
                            className={`p-2.5 rounded-lg transition-colors duration-300 ${
                                activeTab === "TRACK" ? "bg-red-100" : "bg-gray-100"
                            }`}
                        >
                            <img
                                src={runTrack}
                                alt="Running Track"
                                className={`w-6 h-6 transition-all duration-300 ${
                                    activeTab === "TRACK" ? "opacity-100" : "opacity-40 grayscale"
                                }`}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="font-bold text-lg tracking-wide">操場跑道</span>
                            <span className="text-xs text-gray-400 font-medium">Track Running</span>
                        </div>
                    </button>
                </div>
            </div>

            {activeTab === "TRACK" ? (
                <TrackCalculator />
            ) : (
                <div className="timer-section w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6 w-full">
                        {/* 左側：STEP 1 和 STEP 2 (佔 2 份) */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* STEP 1 - 選擇參賽組別 */}
                            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-3 border-b border-blue-200">
                                    <h2 className="flex items-center gap-3 text-lg font-bold text-gray-800">
                                        <span className="inline-flex items-center justify-center px-2 h-7 bg-blue-500 text-white rounded-full text-xs font-bold">
                                            STEP 1
                                        </span>
                                        選擇距離
                                    </h2>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <button
                                            className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-300 ${
                                                selectedGroup === "full"
                                                    ? "border-blue-500 bg-blue-50 shadow-md"
                                                    : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50"
                                            }`}
                                            onClick={() => {
                                                setSelectedGroup("full");
                                                trackButtonClick("通用配速-選擇距離", "全程馬拉松");
                                            }}
                                        >
                                            <img
                                                src={
                                                    selectedGroup === "full"
                                                        ? running_icon_after
                                                        : running_icon_before
                                                }
                                                alt="全程馬拉松"
                                                className="w-14 h-14 mb-2"
                                            />
                                            <p className="text-base font-bold text-gray-800">
                                                全程馬拉松
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">42.195 km</p>
                                        </button>
                                        <button
                                            className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-300 ${
                                                selectedGroup === "half"
                                                    ? "border-blue-500 bg-blue-50 shadow-md"
                                                    : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50"
                                            }`}
                                            onClick={() => {
                                                setSelectedGroup("half");
                                                trackButtonClick("通用配速-選擇距離", "半程馬拉松");
                                            }}
                                        >
                                            <img
                                                src={
                                                    selectedGroup === "half"
                                                        ? running_icon_after
                                                        : running_icon_before
                                                }
                                                alt="半程馬拉松"
                                                className="w-14 h-14 mb-2"
                                            />
                                            <p className="text-base font-bold text-gray-800">
                                                半程馬拉松
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">21.0975 km</p>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* STEP 2 - 設定比賽目標 (Amber Theme) */}
                            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl shadow-md border border-amber-200 overflow-hidden relative">
                                {/* Background decoration */}
                                <div className="absolute top-0 right-0 p-4 opacity-10">
                                    <svg
                                        className="w-24 h-24 text-amber-300"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                                    </svg>
                                </div>

                                <div className="px-3 py-3 border-b border-amber-200/50">
                                    <h2 className="flex items-center gap-3 text-lg font-bold text-amber-900">
                                        <span className="inline-flex items-center justify-center px-2 h-7 bg-amber-500 text-white rounded-full text-xs font-bold shadow-md">
                                            STEP 2
                                        </span>
                                        設定速度與補給
                                    </h2>
                                </div>
                                <div className="p-4 relative">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleSubmit();
                                        }}
                                        className="space-y-4"
                                    >
                                        <div>
                                            <h3 className="text-sm font-semibold text-amber-900 mb-2">
                                                預計完賽時間
                                            </h3>
                                            <div className="flex items-center gap-2 timerInput-wrap">
                                                <input
                                                    type="number"
                                                    value={raceTime.hours}
                                                    onChange={(e) =>
                                                        handleRaceTimeChange(e, "hours", 0, 99)
                                                    }
                                                    placeholder="時"
                                                    className="w-20 px-3 py-2 bg-white border border-amber-300 rounded-lg text-center text-gray-800 placeholder-amber-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                                />
                                                <span className="text-amber-700 font-bold">：</span>
                                                <input
                                                    type="number"
                                                    value={raceTime.minutes}
                                                    onChange={(e) =>
                                                        handleRaceTimeChange(e, "minutes", 0, 59)
                                                    }
                                                    placeholder="分"
                                                    className="w-20 px-3 py-2 bg-white border border-amber-300 rounded-lg text-center text-gray-800 placeholder-amber-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                                />
                                                <span className="text-amber-700 font-bold">：</span>
                                                <input
                                                    type="number"
                                                    value={raceTime.seconds}
                                                    onChange={(e) =>
                                                        handleRaceTimeChange(e, "seconds", 0, 59)
                                                    }
                                                    placeholder="秒"
                                                    className="w-20 px-3 py-2 bg-white border border-amber-300 rounded-lg text-center text-gray-800 placeholder-amber-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-amber-900 mb-2">
                                                進站單站停留秒數
                                            </h3>
                                            <div className="flex items-center gap-3 timerInput-wrap">
                                                <input
                                                    type="number"
                                                    value={raceTime.hydrationTime}
                                                    onChange={(e) =>
                                                        handleRaceTimeChange(
                                                            e,
                                                            "hydrationTime",
                                                            0,
                                                            999
                                                        )
                                                    }
                                                    placeholder="秒"
                                                    className="w-28 px-3 py-2 bg-white border border-amber-300 rounded-lg text-center text-gray-800 placeholder-amber-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    className="px-8 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg result-button"
                                                    onClick={handleSubmit}
                                                >
                                                    計算
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 p-3 bg-amber-100/50 rounded-lg border border-amber-200 stationIcon-wrap">
                                            <p className="text-xs text-amber-800 flex-1">
                                                點選配速表內水杯計算補給時間
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={station_icon_noselect}
                                                    alt="未勾選"
                                                    className="w-5 h-5"
                                                />
                                                <span className="text-amber-600 text-sm">→</span>
                                                <img
                                                    src={station_icon}
                                                    alt="勾選"
                                                    className="w-5 h-5"
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* 右側：STEP 3 (佔 3 份) */}
                        <div className="lg:col-span-3 lg:row-span-2">
                            {/* STEP 3 - 建議配速策略 */}
                            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden h-full">
                                <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-3 py-3 border-b border-purple-200">
                                    <h2 className="flex items-center gap-3 text-xl font-bold text-gray-800">
                                        <span className="inline-flex items-center justify-center px-2 h-7 bg-purple-500 text-white rounded-full text-sm font-bold">
                                            STEP 3
                                        </span>
                                        建議配速策略
                                    </h2>
                                </div>
                                <div className="p-6">
                                    {selectedGroup === "full" ? (
                                        <FullTimer
                                            handleHydrationStationsChange={
                                                handleHydrationStationsChange
                                            }
                                            results={results}
                                            formatTime={formatTime}
                                            formatMMSS={formatMMSS}
                                            raceTime={raceTime}
                                            showYoutubeVideo={showYoutubeVideo}
                                            handleShowMap={handleShowMap}
                                            chrckInput={chrckInput}
                                        />
                                    ) : (
                                        <HalfTimer
                                            handleHydrationStationsChange_half={
                                                handleHydrationStationsChange_half
                                            }
                                            results_half={results_half}
                                            formatTime={formatTime}
                                            formatMMSS={formatMMSS}
                                            raceTime={raceTime}
                                            showYoutubeVideo={showYoutubeVideo}
                                            handleShowMap={handleShowMap}
                                            chrckInput={chrckInput}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BasicPace;
